/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import { diag } from '@opentelemetry/api';

const DIAG_LOGGER = diag.createComponentLogger({
  namespace: '@opentelemetry/browser-instrumentation/utils/request',
});

function isDocument(value: unknown): value is Document {
  return typeof Document !== 'undefined' && value instanceof Document;
}

/**
 * Helper function to determine payload content length for fetch requests
 *
 * The fetch API is kinda messy: there are a couple of ways the body can be passed in.
 *
 * In all cases, the body param can be some variation of ReadableStream,
 * and ReadableStreams can only be read once! We want to avoid consuming the body here,
 * because that would mean that the body never gets sent with the actual fetch request.
 *
 * Either the first arg is a Request object, which can be cloned
 *   so we can clone that object and read the body of the clone
 *   without disturbing the original argument
 *   However, reading the body here can only be done async; the body() method returns a promise
 *   this means this entire function has to return a promise
 *
 * OR the first arg is a url/string
 *   in which case the second arg has type RequestInit
 *   RequestInit is NOT cloneable, but RequestInit.body is writable
 *   so we can chain it into ReadableStream.pipeThrough()
 *
 *   ReadableStream.pipeThrough() lets us process a stream and returns a new stream
 *   So we can measure the body length as it passes through the pipe, but need to attach
 *   the new stream to the original request
 *   so that the browser still has access to the body.
 */
export async function getFetchBodyLength(
  ...args: Parameters<typeof fetch>
): Promise<number | undefined> {
  if (args[0] instanceof URL || typeof args[0] === 'string') {
    const requestInit = args[1];
    if (!requestInit?.body) {
      return undefined;
    }
    if (requestInit.body instanceof ReadableStream) {
      const { body, length } = _getBodyNonDestructively(requestInit.body);
      requestInit.body = body;

      return await length;
    } else {
      return getXHRBodyLength(requestInit.body);
    }
  } else {
    const info = args[0];
    // Request.body has limited availability. ref: https://developer.mozilla.org/en-US/docs/Web/API/Request/body
    // Accessing it here triggers a eslint error from the baseline plugin
    /* eslint-disable baseline-js/use-baseline */
    if (!info?.body) {
      return undefined;
    }
    /* eslint-enable baseline-js/use-baseline */

    const text = await info.clone().text();
    return getByteLength(text);
  }
}

// This function returns the body right away piped to a transform stream
// if available. The length is returned as a promise because the original
// fetch logic has to consume the body to get the final value.
function _getBodyNonDestructively(body: ReadableStream): {
  body: ReadableStream;
  length: Promise<number | undefined>;
} {
  // can't read a ReadableStream without destroying it
  // but we CAN pipe it through and return a new ReadableStream

  // some (older) platforms don't expose the pipeThrough method and in that scenario, we're out of luck;
  //   there's no way to read the stream without consuming it.
  if (!body.pipeThrough) {
    DIAG_LOGGER.warn('Platform has ReadableStream but not pipeThrough!');
    return {
      body,
      length: Promise.resolve(undefined),
    };
  }

  let length = 0;
  let resolveLength: (l: number | undefined) => void;
  const lengthPromise = new Promise<number | undefined>((resolve) => {
    resolveLength = resolve;
  });

  const bodyReader = (body as ReadableStream<Uint8Array>).getReader();
  const bodyWrapper = new ReadableStream<Uint8Array>({
    async pull(controller) {
      const { value, done } = await bodyReader.read();

      length += value?.byteLength || 0;
      if (done) {
        controller.close();
        bodyReader.releaseLock();
        resolveLength(length);
        return;
      }
      controller.enqueue(value);
    },
    cancel(reason) {
      resolveLength(undefined);
      bodyReader.cancel(reason);
    },
  });

  return {
    body: bodyWrapper,
    length: lengthPromise,
  };
}

/**
 * Helper function to determine payload content length for XHR requests
 */
export function getXHRBodyLength(
  body: Document | XMLHttpRequestBodyInit,
): number | undefined {
  if (isDocument(body)) {
    return new XMLSerializer().serializeToString(body).length;
  }

  // XMLHttpRequestBodyInit expands to the following:
  if (typeof body === 'string') {
    return getByteLength(body);
  }

  if (body instanceof Blob) {
    return body.size;
  }

  if (body instanceof FormData) {
    return getFormDataSize(body);
  }

  if (body instanceof URLSearchParams) {
    return getByteLength(body.toString());
  }

  // ArrayBuffer | ArrayBufferView
  if (body.byteLength !== undefined) {
    return body.byteLength;
  }

  DIAG_LOGGER.warn('unknown body type');
  return undefined;
}

const TEXT_ENCODER = new TextEncoder();
function getByteLength(s: string): number {
  return TEXT_ENCODER.encode(s).byteLength;
}

function getFormDataSize(formData: FormData): number {
  let size = 0;
  for (const [key, value] of formData.entries()) {
    size += key.length;
    if (value instanceof Blob) {
      size += value.size;
    } else {
      size += value.length;
    }
  }
  return size;
}

const DEFAULT_KNOWN_METHODS = {
  CONNECT: true,
  DELETE: true,
  GET: true,
  HEAD: true,
  OPTIONS: true,
  PATCH: true,
  POST: true,
  PUT: true,
  TRACE: true,
  // QUERY from https://datatracker.ietf.org/doc/draft-ietf-httpbis-safe-method-w-body/
  QUERY: true,
};
/**
 * Normalize an HTTP request method string per `http.request.method` spec
 * https://github.com/open-telemetry/semantic-conventions/blob/main/docs/http/http-spans.md#http-client-span
 */
export function normalizeHttpRequestMethod(method: string): string {
  const methUpper = method.toUpperCase();
  if (Object.hasOwn(DEFAULT_KNOWN_METHODS, methUpper)) {
    return methUpper;
  }
  return '_OTHER';
}
